// FunctionalSpecification.js — Documentation Fonctionnelle : POC « Sessions d'audit » (module Suivi d'audit).
// Réplique INTERACTIVE et fidèle de l'écran réel des sessions : liste paginée (code, domaine, module,
// statut, score de conformité avec jauge 70/85, progression workflow 7 étapes), filtres, détail
// (métadonnées + fichier source + parcours workflow RECEPTION→COMPLETED) et IMPORT XML (zone de dépôt
// avec nomenclature expliquée) — DOUBLÉE d'un guide utilisateur pas-à-pas. Actions SIMULÉES localement.
// Source de vérité : scanbank-frontend « [SCANBANK] Suivi Audit/[SUI][01]-Sessions.js » + scanbank_core
// AuditScanBankController (/api/scanbank/audit/sessions), AuditFileUploadController (/audit/files/upload),
// AuditSessionWorkflowController (/api/scanbank/workflow/audit-session) et AuditJobHandler (scan 10 s).
import React, { useState } from 'react';
import {
  Activity, Upload, X, CheckCircle, XCircle, Search, Info, ArrowRight, Clock, Loader,
  CheckCircle2, FileText, Box, Layers, Target, GitBranch, AlertTriangle, Copy, Eye,
  ChevronLeft, ChevronRight, FileUp, RotateCcw,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Tabs } from '../ui';

// Statuts d'une session (enum backend AuditStatus — libellés FR de l'écran réel)
const STATUTS = {
  PENDING: { label: 'En attente', color: '#EAB308', icon: Clock },
  IN_PROGRESS: { label: 'En cours', color: '#3B82F6', icon: Loader },
  COMPLETED: { label: 'Terminée', color: '#10B981', icon: CheckCircle2 },
  FAILED: { label: 'Échec', color: '#EF4444', icon: XCircle },
};

// Les 7 étapes automatiques du workflow SCANBANK_AUDIT_SESSION (ordre nominal backend)
const WORKFLOW_STEPS = [
  { id: 'RECEPTION', step: 1, label: 'Réception', icon: Box, description: 'Fichier détecté, nomenclature vérifiée, attributs racine du XML lus (font autorité)' },
  { id: 'PARSING', step: 2, label: 'Parsing', icon: FileText, description: 'Lecture StAX des blocs Control / Metrics / Rule / Anomaly (lots de 500)' },
  { id: 'VALIDATION', step: 3, label: 'Validation', icon: CheckCircle, description: 'Validation XSD ScanBank v1.0 en streaming + entrées AuditData' },
  { id: 'INTEGRATION', step: 4, label: 'Intégration', icon: Layers, description: 'Recommandations générées pour les anomalies CRITICAL / MAJOR' },
  { id: 'ANALYSE', step: 5, label: 'Analyse', icon: Search, description: 'Agrégats SQL : exécutions, anomalies, répartitions' },
  { id: 'SCORING', step: 6, label: 'Scoring', icon: Target, description: 'Calcul du score de conformité et du niveau de risque' },
  { id: 'COMPLETED', step: 7, label: 'Terminé', icon: CheckCircle2, description: 'Session COMPLETED, notifications, fichier archivé vers OUTPUT/{sessionCode}/' },
];

const DOMAINES = [
  { code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude — BMCI', environment: 'PROD' },
  { code: 'CBS_AMPLITUDE_UAT', name: 'Amplitude — Recette BMCI', environment: 'UAT' },
];
const MODULES = ['COMPTABILITE', 'CREDITS', 'CLIENTS', 'DEPOTS', 'VIREMENTS', 'HABILITATION', 'TRESORERIE', 'RISQUES'];

// Nomenclature plausible d'un fichier XML CBS (ancrée à droite — la validation stricte reste backend)
const XML_FILE_NAME_REGEX = /^[A-Z0-9_]+_[A-Z0-9]+_\d{8}\.xml$/i;

// Échantillon réaliste : une journée d'audit du CBS Amplitude (fichiers GENERO, 1 fichier par rapport)
const SAMPLE = [
  { id: 1, sessionCode: 'SES_CBS_AMPLITUDE_COMPTABILITE_20260712060012_4F8A2C1D', domain: DOMAINES[0], module: 'COMPTABILITE', status: 'COMPLETED', wfStep: 7, score: 92.5, totalProcessed: 15230, totalAnomalies: 3, startTime: '2026-07-12 06:00', endTime: '2026-07-12 06:04', triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_COMPTABILITE_CPTA001_PROD_20260712.xml', failReason: null },
  { id: 2, sessionCode: 'SES_CBS_AMPLITUDE_CREDITS_20260712061548_B77E90AF', domain: DOMAINES[0], module: 'CREDITS', status: 'COMPLETED', wfStep: 7, score: 64.2, totalProcessed: 3120, totalAnomalies: 41, startTime: '2026-07-12 06:15', endTime: '2026-07-12 06:22', triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_CREDITS_CRD014_PROD_20260712.xml', failReason: null },
  { id: 3, sessionCode: 'SES_CBS_AMPLITUDE_CLIENTS_20260712062210_9C3D11EE', domain: DOMAINES[0], module: 'CLIENTS', status: 'IN_PROGRESS', wfStep: 5, score: null, totalProcessed: 5210, totalAnomalies: null, startTime: '2026-07-12 06:22', endTime: null, triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_CLIENTS_CLI005_PROD_20260712.xml', failReason: null },
  { id: 4, sessionCode: 'SES_CBS_AMPLITUDE_VIREMENTS_20260711235906_D0AA41B2', domain: DOMAINES[0], module: 'VIREMENTS', status: 'FAILED', wfStep: 3, score: null, totalProcessed: 0, totalAnomalies: null, startTime: '2026-07-11 23:59', endTime: '2026-07-12 00:01', triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_VIREMENTS_VIR002_PROD_20260711.xml', failReason: 'Validation XSD : élément <Metrics> manquant (ligne 214) — fichier déplacé vers ERROR + .reason' },
  { id: 5, sessionCode: 'SES_CBS_AMPLITUDE_DEPOTS_20260711180244_77C2F0B3', domain: DOMAINES[0], module: 'DEPOTS', status: 'COMPLETED', wfStep: 7, score: 78.0, totalProcessed: 8735, totalAnomalies: 12, startTime: '2026-07-11 18:02', endTime: '2026-07-11 18:09', triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_DEPOTS_DEP003_PROD_20260711.xml', failReason: null },
  { id: 6, sessionCode: 'SES_CBS_AMPLITUDE_UAT_HABILITATION_20260710091530_1A2B3C4D', domain: DOMAINES[1], module: 'HABILITATION', status: 'COMPLETED', wfStep: 7, score: 88.4, totalProcessed: 412, totalAnomalies: 5, startTime: '2026-07-10 09:15', endTime: '2026-07-10 09:16', triggeredBy: 'Manual', sourceFile: 'CBS_AMPLITUDE_UAT_HABILITATION_HAB001_UAT_20260710.xml', failReason: null },
  { id: 7, sessionCode: 'SES_CBS_AMPLITUDE_RISQUES_20260712063001_E5F60718', domain: DOMAINES[0], module: 'RISQUES', status: 'PENDING', wfStep: 0, score: null, totalProcessed: null, totalAnomalies: null, startTime: '2026-07-12 06:30', endTime: null, triggeredBy: 'SFTP', sourceFile: 'CBS_AMPLITUDE_RISQUES_RSQ003_PROD_20260712.xml', failReason: null },
];

const PAGE_SIZE = 5;

// Génération du code session — même règle que le backend AuditJobHandler :
// SES_{domaine}_{module}_{yyyyMMddHHmmss}_{UUID8}
const genererSessionCode = (domainCode, module) => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const uuid8 = Math.random().toString(16).slice(2, 10).toUpperCase().padEnd(8, '0');
  return `SES_${domainCode}_${module}_${ts}_${uuid8}`;
};

// ---- Petits sous-composants de la réplique ---------------------------------
const StatutBadge = ({ status }) => {
  const s = STATUTS[status] || { label: status, color: '#6B7280', icon: Clock };
  const Ic = s.icon;
  return <Badge color={s.color}><Ic className="h-3 w-3" />{s.label}</Badge>;
};

// Jauge de score colorée selon les seuils de conformité : CRITIQUE < 70, ALERTE < 85, ACCEPTABLE ≥ 85
const ScoreGauge = ({ score, showLevel = false }) => {
  if (score == null) return <span className="text-sm text-gray-400">—</span>;
  const color = score >= 85 ? '#10B981' : score >= 70 ? '#EAB308' : '#EF4444';
  const niveau = score >= 85 ? 'ACCEPTABLE' : score >= 70 ? 'ALERTE' : 'CRITIQUE';
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden inline-block">
        <span className="block h-1.5 rounded-full" style={{ width: `${Math.min(score, 100)}%`, backgroundColor: color }} />
      </span>
      <span className="text-sm font-bold" style={{ color }}>{score}%</span>
      {showLevel && <Badge color={color}>{niveau}</Badge>}
    </span>
  );
};

// Progression compacte du workflow (colonne du tableau) : 7 segments + n/7
const WorkflowCompact = ({ session }) => {
  if (session.status === 'PENDING') return <span className="text-xs text-gray-400 italic">Non démarré</span>;
  if (session.status === 'FAILED') return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600"><XCircle className="h-3.5 w-3.5" />Échec ({session.wfStep}/7)</span>;
  if (session.wfStep >= 7) return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />7/7</span>;
  return (
    <span className="inline-flex items-center gap-2 min-w-[120px]">
      <span className="flex-1 flex gap-0.5">
        {WORKFLOW_STEPS.map((s) => (
          <span key={s.id} title={s.label} className={`h-1.5 w-3 rounded-full ${s.step < session.wfStep ? 'bg-emerald-500' : s.step === session.wfStep ? 'bg-blue-500 animate-pulse' : 'bg-gray-200 dark:bg-gray-700'}`} />
        ))}
      </span>
      <span className="text-xs text-gray-500 whitespace-nowrap">{session.wfStep}/7</span>
    </span>
  );
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none';

// ---- Page -------------------------------------------------------------------
const SessionsPOC = () => {
  const [sessions, setSessions] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(SAMPLE[0].id);
  const [mode, setMode] = useState('detail'); // 'detail' | 'import'
  const [tab, setTab] = useState('info'); // 'info' | 'workflow'
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [page, setPage] = useState(0);
  const [notice, setNotice] = useState(null);
  const [importForm, setImportForm] = useState({ domainCode: DOMAINES[0].code, module: '', fileName: '' });
  const [importState, setImportState] = useState('idle'); // 'idle' | 'sending'

  const selected = sessions.find((s) => s.id === selectedId) || null;
  const filtered = sessions.filter((s) =>
    (filterStatus === 'all' || s.status === filterStatus) &&
    (filterModule === 'all' || s.module === filterModule) &&
    (`${s.sessionCode} ${s.sourceFile} ${s.domain.code}`.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const flash = (msg) => { setNotice(msg); window.setTimeout(() => setNotice(null), 4000); };
  const selectRow = (s) => { setSelectedId(s.id); setMode('detail'); setTab('info'); };

  const nomenclatureOk = XML_FILE_NAME_REGEX.test(importForm.fileName.trim());
  const importValide = importForm.domainCode && importForm.module && nomenclatureOk;

  // Dépôt simulé : en réel le fichier part vers le répertoire d'entrée du batch, puis
  // l'AuditJobHandler (scan toutes les 10 s) crée la session et déroule les 7 étapes.
  const submitImport = () => {
    if (!importValide || importState === 'sending') return;
    setImportState('sending');
    window.setTimeout(() => {
      const code = genererSessionCode(importForm.domainCode, importForm.module);
      const domain = DOMAINES.find((d) => d.code === importForm.domainCode) || DOMAINES[0];
      const id = Math.max(0, ...sessions.map((s) => s.id)) + 1;
      setSessions((prev) => [{ id, sessionCode: code, domain, module: importForm.module, status: 'PENDING', wfStep: 0, score: null, totalProcessed: null, totalAnomalies: null, startTime: 'à l\'instant', endTime: null, triggeredBy: 'Manual', sourceFile: importForm.fileName.trim(), failReason: null }, ...prev]);
      setImportState('idle'); setMode('detail'); setSelectedId(id); setPage(0);
      flash(`Fichier ${importForm.fileName.trim()} déposé — session ${code} créée par le batch (délai réel ≈ 10 s)`);
      setImportForm({ domainCode: DOMAINES[0].code, module: '', fileName: '' });
    }, 800);
  };

  // Reprise simulée d'une session en échec (événement REPRENDRE : ECHEC → INITIAL)
  const reprendre = (s) => {
    setSessions((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: 'PENDING', wfStep: 0, failReason: null } : x)));
    flash(`Workflow relancé pour la session ${s.sessionCode}`);
  };

  return (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle — Sessions d'audit</h1>
          <Badge color="#3B82F6">Module Suivi d'audit</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Sessions d'Audit</strong> : liste paginée, score de conformité, progression du workflow 7 étapes et import de fichiers XML CBS. <span className="text-gray-500 dark:text-gray-400">Les actions ci-dessous sont simulées localement (démo) — aucun appel réseau.</span></p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-bold leading-tight">Sessions d'Audit</h2>
              <p className="text-xs text-white/80">Suivi des sessions d'audit CBS et progression workflow — {sessions.length} session(s)</p>
            </div>
          </div>
          <Button onClick={() => setMode('import')} className="bg-white/20 hover:bg-white/30 text-white"><Upload className="h-4 w-4" />Importer XML</Button>
        </div>

        {notice && (
          <div className="mx-4 mt-3 flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" /><span className="break-all">{notice}</span>
          </div>
        )}

        {mode === 'import' ? (
          /* ===== Vue import XML : zone de dépôt + nomenclature expliquée ===== */
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Upload className="h-5 w-5 text-blue-500" />Importer un fichier XML</h3>
              <button onClick={() => setMode('detail')} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-4">
                <label className="text-sm block">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Domaine d'Audit <span className="text-red-500">*</span></span>
                  <select value={importForm.domainCode} onChange={(e) => setImportForm((f) => ({ ...f, domainCode: e.target.value }))} className={inputCls}>
                    {DOMAINES.map((d) => <option key={d.code} value={d.code}>{d.name} ({d.code})</option>)}
                  </select>
                </label>
                <label className="text-sm block">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Module <span className="text-red-500">*</span></span>
                  <select value={importForm.module} onChange={(e) => setImportForm((f) => ({ ...f, module: e.target.value }))} className={inputCls}>
                    <option value="">— Sélectionner un module —</option>
                    {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                {/* Zone de dépôt : dans le POC le fichier est représenté par son nom */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-5 text-center">
                  <FileUp className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 mb-2">Glissez-déposez votre fichier XML ici (démo : saisir le nom du fichier)</p>
                  <input value={importForm.fileName} onChange={(e) => setImportForm((f) => ({ ...f, fileName: e.target.value }))}
                    placeholder="CBS_AMPLITUDE_COMPTABILITE_CPTA001_PROD_20260712.xml" className={`${inputCls} font-mono text-xs`} />
                  {importForm.fileName && !nomenclatureOk && (
                    <p className="text-xs text-red-500 mt-2 text-left">Nomenclature invalide. Format attendu : {'{SYSTEM}_{MODULE}[_{REPORT}]_{ENV}_{YYYYMMDD}'}.xml</p>
                  )}
                  {importForm.fileName && nomenclatureOk && (
                    <p className="text-xs text-emerald-600 mt-2 text-left flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />Nomenclature valide</p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setMode('detail')}>Annuler</Button>
                  <Button onClick={submitImport} disabled={!importValide || importState === 'sending'}>
                    {importState === 'sending' ? <>Dépôt en cours…</> : <><Upload className="h-4 w-4" />Importer</>}
                  </Button>
                </div>
              </div>
              {/* Nomenclature expliquée */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2"><Info className="h-4 w-4 text-blue-500" />Nomenclature des fichiers XML CBS</h4>
                <p className="text-xs text-gray-500 mb-3">Lecture <strong>ancrée par la droite</strong> : date, environnement, éventuel code rapport, module — le reste forme le SYSTEM (qui peut contenir des underscores). Les attributs racine du XML font autorité à la réception.</p>
                <div className="rounded-lg bg-gray-900 p-3 mb-3 overflow-x-auto">
                  <code className="text-xs text-green-400 whitespace-nowrap">CBS_AMPLITUDE_COMPTABILITE_CPTA001_PROD_20260712.xml</code>
                </div>
                <ul className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
                  <li><Badge color="#8B5CF6">SYSTEM</Badge> <code>CBS_AMPLITUDE</code> — code du domaine d'audit</li>
                  <li><Badge color="#3B82F6">MODULE</Badge> <code>COMPTABILITE</code> — module CBS audité</li>
                  <li><Badge color="#F97316">REPORT</Badge> <code>CPTA001</code> — code du rapport GENERO (optionnel, 1 fichier par rapport)</li>
                  <li><Badge color="#10B981">ENV</Badge> <code>PROD</code> — PROD, UAT, DEV ou PREPROD</li>
                  <li><Badge color="#EAB308">DATE</Badge> <code>20260712</code> — AAAAMMJJ</li>
                </ul>
                <p className="text-xs text-gray-400 mt-3">Le fichier est déposé dans le répertoire d'entrée du batch : la session est créée automatiquement au prochain passage du scan (toutes les 10 s, max 10 fichiers par lot).</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Filtres */}
            <div className="p-4 flex flex-wrap items-center gap-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Rechercher une session (code, fichier)..." className={`${inputCls} pl-10`} />
              </div>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }} className={`${inputCls} w-auto`}>
                <option value="all">Tous les statuts</option>
                {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filterModule} onChange={(e) => { setFilterModule(e.target.value); setPage(0); }} className={`${inputCls} w-auto`}>
                <option value="all">Tous les modules</option>
                {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {/* Liste paginée */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  <tr>{['Code Session', 'Domaine', 'Module', 'Statut', 'Workflow', 'Score', 'Anomalies', 'Date'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2 whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                  {pageRows.map((s) => {
                    const on = selected?.id === s.id;
                    return (
                      <tr key={s.id} onClick={() => selectRow(s)} className={`cursor-pointer transition-colors ${on ? 'bg-blue-50 dark:bg-blue-500/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-mono font-medium text-gray-900 dark:text-white text-xs truncate max-w-[260px]" title={s.sessionCode}>{s.sessionCode}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[260px]">{s.sourceFile}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-gray-900 dark:text-white">{s.domain.name}</div>
                          <div className="text-xs text-gray-500">{s.domain.environment}</div>
                        </td>
                        <td className="px-4 py-2.5"><Badge color="#3B82F6">{s.module}</Badge></td>
                        <td className="px-4 py-2.5"><StatutBadge status={s.status} /></td>
                        <td className="px-4 py-2.5"><WorkflowCompact session={s} /></td>
                        <td className="px-4 py-2.5"><ScoreGauge score={s.score} /></td>
                        <td className="px-4 py-2.5">
                          {s.totalAnomalies == null ? <span className="text-gray-400">—</span>
                            : s.totalAnomalies > 0
                              ? <span className="inline-flex items-center gap-1 text-red-600 font-medium"><AlertTriangle className="h-3.5 w-3.5" />{s.totalAnomalies}</span>
                              : <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle className="h-3.5 w-3.5" />0</span>}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-xs">{s.startTime}</td>
                      </tr>
                    );
                  })}
                  {pageRows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Aucune session ne correspond aux critères</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>{filtered.length} session(s) — page {page + 1} / {totalPages}</span>
              <span className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" />Précédent</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Suivant<ChevronRight className="h-4 w-4" /></Button>
              </span>
            </div>

            {/* Détail de la session sélectionnée */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-5">
              {selected ? (
                <div>
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-12 w-12 rounded-xl bg-blue-500 flex items-center justify-center shrink-0"><GitBranch className="h-6 w-6 text-white" /></span>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white font-mono truncate flex items-center gap-2">{selected.sessionCode}<Copy className="h-3.5 w-3.5 text-gray-400 shrink-0" /></h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
                          <StatutBadge status={selected.status} />
                          <ScoreGauge score={selected.score} showLevel />
                        </div>
                      </div>
                    </div>
                    {selected.status === 'FAILED' && (
                      <Button variant="outline" size="sm" onClick={() => reprendre(selected)}><RotateCcw className="h-4 w-4" />Reprendre</Button>
                    )}
                  </div>

                  <Tabs tabs={[{ id: 'info', label: 'Informations' }, { id: 'workflow', label: 'Progression Workflow' }]} active={tab} onChange={setTab} className="mb-4" />

                  {tab === 'info' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {[['Domaine', `${selected.domain.name} (${selected.domain.environment})`], ['Module', selected.module], ['Déclenché par', selected.triggeredBy], ['Fichier source', selected.sourceFile], ['Début', selected.startTime], ['Fin', selected.endTime || '—'], ['Éléments traités', selected.totalProcessed != null ? selected.totalProcessed.toLocaleString('fr-FR') : '—'], ['Anomalies détectées', selected.totalAnomalies != null ? selected.totalAnomalies : '—']].map(([l, v], i) => (
                        <div key={i} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{l}</div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{v}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selected.status === 'PENDING' && <p className="text-sm text-gray-500 italic mb-2">Workflow non démarré — la session attend le passage du batch.</p>}
                      {WORKFLOW_STEPS.map((s) => {
                        const isPast = selected.wfStep > s.step || (selected.status === 'COMPLETED' && selected.wfStep >= 7);
                        const isCurrent = selected.wfStep === s.step && selected.status !== 'COMPLETED';
                        const isError = isCurrent && selected.status === 'FAILED';
                        const Ic = s.icon;
                        return (
                          <div key={s.id} className={`flex items-center p-2.5 rounded-lg border ${isError ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30' : isCurrent ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30' : isPast ? 'bg-emerald-50/60 dark:bg-emerald-500/5 border-transparent' : 'border-transparent opacity-50'}`}>
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 shrink-0 ${isError ? 'bg-red-500' : isPast ? 'bg-emerald-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                              {isPast ? <CheckCircle2 className="h-4 w-4 text-white" /> : isError ? <XCircle className="h-4 w-4 text-white" /> : isCurrent ? <Loader className="h-4 w-4 text-white animate-spin" /> : <Ic className="h-4 w-4 text-gray-100" />}
                            </span>
                            <span className="min-w-0">
                              <span className={`block text-sm font-medium ${isError ? 'text-red-700 dark:text-red-300' : isCurrent ? 'text-blue-700 dark:text-blue-300' : isPast ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500'}`}>{s.step}. {s.label}</span>
                              <span className="block text-xs text-gray-500">{s.description}</span>
                            </span>
                          </div>
                        );
                      })}
                      {selected.failReason && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-sm text-red-700 dark:text-red-300">
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                          <span><strong>Motif de l'échec (.reason)</strong> : {selected.failReason} — l'événement <strong>REPRENDRE</strong> réinitialise le workflow (ECHEC → INITIAL).</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-400 gap-2"><Eye className="h-4 w-4" />Sélectionnez une session dans la liste</div>
              )}
            </div>
          </>
        )}
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Upload className="h-5 w-5 text-blue-500" />Importer un fichier XML</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Importer XML »</strong> — c'est LE point d'entrée : une session naît exclusivement d'un dépôt de fichier. <button onClick={() => setMode('import')} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Sélectionner le <strong>Domaine d'Audit</strong> puis le <strong>Module</strong>.</GuideStep>
              <GuideStep n={3}>Déposer un fichier <strong>.xml</strong> respectant la nomenclature <code>{'{SYSTEM}_{MODULE}[_{REPORT}]_{ENV}_{YYYYMMDD}'}.xml</code>.</GuideStep>
              <GuideStep n={4}>Valider : le fichier part dans le répertoire d'entrée du batch.</GuideStep>
              <GuideStep n={5}>La session apparaît après le passage du scan (≈ 10 s) avec un code généré <code>SES_…</code>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><GitBranch className="h-5 w-5 text-blue-500" />Suivre une session</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Filtrer par <strong>statut</strong> (En attente, En cours, Terminée, Échec) ou <strong>module</strong>, ou rechercher par code/fichier.</GuideStep>
              <GuideStep n={2}>La colonne <strong>Workflow</strong> montre la progression n/7 des étapes automatiques.</GuideStep>
              <GuideStep n={3}>Cliquer sur une ligne → onglet <strong>Progression Workflow</strong> pour le détail des 7 étapes. <button onClick={() => { setMode('detail'); setTab('workflow'); }} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
              <GuideStep n={4}>En cas d'<strong>Échec</strong>, le motif (.reason) est affiché ; le bouton <strong>Reprendre</strong> relance le workflow.</GuideStep>
              <GuideStep n={5}>Une session <strong>Terminée</strong> donne accès au rapport (module Rapports & Supervision).</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-blue-500" />Comprendre le score</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Le score est calculé à l'étape <strong>Scoring</strong> : <code>100 − (CRIT×10 + MAJ×5 + MIN×1) / NbContrôles × 10</code>, borné [0, 100].</GuideStep>
              <GuideStep n={2}><span className="text-emerald-600 font-medium">ACCEPTABLE</span> : score ≥ 85 — conformité satisfaisante.</GuideStep>
              <GuideStep n={3}><span className="text-yellow-600 font-medium">ALERTE</span> : 70 ≤ score &lt; 85 — plan d'action recommandé.</GuideStep>
              <GuideStep n={4}><span className="text-red-600 font-medium">CRITIQUE</span> : score &lt; 70 — traitement prioritaire des anomalies.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence des champs ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-blue-500" />Référence des champs (liste et import)</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['Code Session', 'Généré', '—', 'SES_{domaine}_{module}_{yyyyMMddHHmmss}_{UUID8} — unique, généré par le batch'],
                ['Domaine d\'Audit (import)', 'Liste', 'Oui', 'Cohérence vérifiée avec le SYSTEM déduit du nom de fichier'],
                ['Module (import)', 'Liste', 'Oui', 'Référentiel AuditModule du domaine sélectionné'],
                ['Fichier XML (import)', 'Fichier', 'Oui', 'Extension .xml + nomenclature {SYSTEM}_{MODULE}[_{REPORT}]_{ENV}_{YYYYMMDD}.xml'],
                ['Statut', 'Enum', '—', 'PENDING, IN_PROGRESS, COMPLETED, FAILED (AuditStatus)'],
                ['Score de conformité', 'Décimal (5,2)', '—', 'Jauge colorée : rouge < 70, jaune < 85, vert ≥ 85'],
                ['Déclenché par', 'Texte', '—', 'Scheduler, Manual, API ou SFTP'],
                ['Début / Fin', 'Horodatage', '—', 'startTime / endTime de la session'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>{r[2]}</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une session naît <strong>exclusivement du dépôt d'un fichier XML</strong> (import IHM ou dépôt direct SFTP) — la création manuelle a été retirée.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le batch (scan toutes les <strong>10 s</strong>, max 10 fichiers/lot, verrou PostgreSQL mono-instance) génère le code <code>SES_…</code> puis déroule les <strong>7 étapes automatiques</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Les <strong>attributs racine du XML font autorité</strong> à la réception (domaine/module/environnement) ; auto-provisioning des domaines et modules inconnus.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Score = <code>100 − (CRITICAL×10 + MAJOR×5 + MINOR×1)/NbContrôles×10</code> ; CRITIQUE &lt; 70, ALERTE &lt; 85, ACCEPTABLE ≥ 85.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Session <strong>COMPLETED</strong> : fichier archivé vers <code>OUTPUT/{'{sessionCode}'}/</code> ; en échec : <code>ERROR</code> + fichier <code>.reason</code> ; reprise possible (événement REPRENDRE).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />La <strong>suppression</strong> d'une session n'est proposée que pour les statuts PENDING et FAILED.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Méthode', 'Endpoint', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200 font-mono">
                {[
                  ['GET', '#10B981', '/api/scanbank/audit/sessions/page?page,size,sort,search,status,module,domainCode', 'SCANBANK_AUDIT_VIEW'],
                  ['GET', '#10B981', '/api/scanbank/audit/sessions/stats', 'SCANBANK_AUDIT_VIEW'],
                  ['GET', '#10B981', '/api/scanbank/audit/sessions/{id} | /code/{sessionCode}', 'SCANBANK_AUDIT_VIEW'],
                  ['DELETE', '#EF4444', '/api/scanbank/audit/sessions/{id}', 'SCANBANK_AUDIT_DELETE'],
                  ['POST', '#3B82F6', '/api/scanbank/audit/files/upload (multipart file, 201)', 'SCANBANK_AUDIT_CREATE'],
                  ['POST', '#3B82F6', '/api/scanbank/workflow/audit-session/demarrer?sessionCode=&sourceFile=&domainCode=', 'SCANBANK_WORKFLOW_START'],
                  ['POST', '#3B82F6', '/api/scanbank/workflow/audit-session/{ref}/executer | /etape?event= | /echec | /reprendre', 'SCANBANK_WORKFLOW_EXECUTE'],
                  ['GET', '#10B981', '/api/scanbank/workflow/audit-session/{ref}/statut', 'SCANBANK_WORKFLOW_VIEW'],
                  ['POST', '#3B82F6', '/api/scanbank/workflow/audit-session/statuts (batch, max 200 codes)', 'SCANBANK_WORKFLOW_VIEW'],
                  ['GET', '#10B981', '/api/scanbank/export/sessions?format=csv&<filtres>', 'SCANBANK_AUDIT_VIEW'],
                ].map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5"><Badge color={r[1]}>{r[0]}</Badge></td>
                    <td className="px-3 py-1.5 break-all">{r[2]}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : scanbank_core / AuditScanBankController + AuditFileUploadController + workflow SCANBANK_AUDIT_SESSION</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default SessionsPOC;
